import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin, { Draggable } from '@fullcalendar/interaction';
import esLocale from '@fullcalendar/core/locales/es';
import Swal from 'sweetalert2';
import { format } from '@formkit/tempo';
import { alertaCargandoProceso, alertaGeneral } from '../../utils/alertas';
import '../../styles/CalendarDisponibility.css';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../utils/axiosPetition';
import SvgIcon from '../../utils/Icon';
import { ReactComponent as Calendario } from '../../assets/icons/calendario.svg';
import Cargando from '../../utils/Cargando';
import { useDataUser } from '../../context/dataUserContext';

// Convierte "09:00" (24h) a { hour12: 9, minute: 0, ampm: 'AM' }
function time24To12(time24) {
    if (!time24 || !time24.match(/^\d{1,2}:\d{2}$/)) return { hour12: 9, minute: 0, ampm: 'AM' };
    const [h, m] = time24.split(':').map(Number);
    const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const ampm = h < 12 ? 'AM' : 'PM';
    return { hour12, minute: m, ampm };
}
// Convierte hour12 (1-12), minute (0-59), ampm ('AM'|'PM') a "HH:mm"
function time12To24(hour12, minute, ampm) {
    let h = Number(hour12) || 9;
    const m = Math.min(59, Math.max(0, Number(minute) || 0));
    if (ampm === 'AM') h = h === 12 ? 0 : h;
    else h = h === 12 ? 12 : h + 12;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

const HOURS_12 = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
// Citas de máximo 20 min → bloques cada 20 min
const MINUTES_OPTIONS = [0, 20, 40];
function minuteToSlot(m) {
    return MINUTES_OPTIONS.reduce((a, b) => Math.abs(a - m) <= Math.abs(b - m) ? a : b);
}
function formatTime12(time24) {
    const { hour12, minute, ampm } = time24To12(time24);
    return `${hour12}:${String(minute).padStart(2, '0')} ${ampm}`;
}
function capitalize(str) {
    return str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : '';
}

const CalendarDisponibility = ({ doctorId, highlightSection = false }) => {
    const bigCalendarRef = useRef(null);
    const [events, setEvents] = useState([]);
    const [diasSemana] = useState(['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado']);
    const [showExternalEvents, setShowExternalEvents] = useState(false);
    const [horarioOficina, setHorarioOficina] = useState([]);
    const [horarioEspecifico, setHorarioEspecifico] = useState([]);
    const [refresh, setRefresh] = useState(false);
    const [cargando, setCargando] = useState(true);
    const [calendarView, setCalendarView] = useState("dayGridMonth");
    const [newHorario, setNewHorario] = useState({
        dia: 'lunes',
        horaInicio: '09:00',
        horaFin: '17:00',
        tipo: 'fijo',
        soloUnaFecha: false,
        fechaEspecifica: new Date().toISOString().slice(0, 10)
    });
    const [horarioAgregadoMsg, setHorarioAgregadoMsg] = useState(null);
    const [showModalAgregarHorario, setShowModalAgregarHorario] = useState(false);
    const timeoutAgregadoRef = useRef(null);

    // console.log("Horario Oficina:", horarioOficina);
    // console.log("Horario Especifico:", horarioEspecifico);

    // Vista inicial: Mes. Los botones Mes / Semana / Día están en headerToolbar

    useEffect(() => () => {
        if (timeoutAgregadoRef.current) clearTimeout(timeoutAgregadoRef.current);
    }, []);

    useEffect(() => {
        apiClient.get(`/api/doctor/getCalendar/${doctorId}`)
            .then((res) => {
                const { horariosOficina, horariosEspecificos } = res.data;
                setHorarioOficina(horariosOficina);
                setHorarioEspecifico(horariosEspecificos);
                // console.log(res);
                setCargando(false);
            })
            .catch(error => {
                alertaGeneral({
                    titulo: 'Error',
                    messageHtml: error.response?.data?.message || 'No se pudieron cargar los horarios',
                    isError: true
                })
            })
    }, [refresh]);

    useEffect(() => {
        const newEvents = [];
        const diasSemanaLocal = {
            "lunes": 1,
            "martes": 2,
            "miercoles": 3,
            "jueves": 4,
            "viernes": 5,
            "sabado": 6,
            "domingo": 0
        };

        const toTimeString = (h) => {
            if (!h || typeof h !== 'string') return '09:00:00';
            const parts = h.trim().split(':');
            if (parts.length >= 3) return h;
            if (parts.length === 2) return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}:00`;
            return '09:00:00';
        };
        // Obtener el próximo día de la semana (0=dom, 1=lun, ...)
        const getNextDateForDay = (dayOfWeek, weeksAhead = 0) => {
            const d = new Date();
            const currentDay = d.getDay();
            let diff = (dayOfWeek - currentDay + 7) % 7;
            if (diff === 0) diff = 7;
            d.setDate(d.getDate() + diff + weeksAhead * 7);
            return d;
        };
        const formatDateISO = (d) => d.toISOString().slice(0, 10);
        // Procesar horarios de oficina: usar eventos recurrentes simples (UN evento por día/intervalo)
        horarioOficina && horarioOficina.forEach(horario => {
            const dia = diasSemanaLocal[horario.dia.toLowerCase()];
            if (dia === undefined) return;
            horario.intervalos.forEach(intervalo => {
                const startTime = toTimeString(intervalo.inicio);
                const endTime = toTimeString(intervalo.fin);
                newEvents.push({
                    id: `fijo-${horario.dia}-${intervalo.inicio}-${intervalo.fin}`,
                    title: 'Disponible',
                    tipo: 'fijo',
                    daysOfWeek: [dia],
                    startTime,
                    endTime,
                    allDay: false,
                    eventDisplay: 'block',
                    className: 'wrap-event-oficina',
                    backgroundColor: 'rgb(33, 196, 93)',
                    borderColor: 'rgb(33, 196, 93)',
                    textColor: '#ffffff',
                    editable: true,
                });
            });
        });

        // Procesar horarios específicos (con start/end como Date para bloque vertical correcto)
        horarioEspecifico && horarioEspecifico.forEach(horario => {
            const diaEspecifico = new Date(horario.dia);
            horario.intervalos.forEach(intervalo => {
                const startTimeStr = toTimeString(intervalo.inicio);
                const endTimeStr = toTimeString(intervalo.fin);
                const [sh, sm] = startTimeStr.split(':').map(Number);
                const [eh, em] = endTimeStr.split(':').map(Number);
                const start = new Date(diaEspecifico);
                start.setHours(sh, sm, 0, 0);
                const end = new Date(diaEspecifico);
                end.setHours(eh, em, 0, 0);
                newEvents.push({
                    id: `especifico-${horario.dia}-${intervalo.inicio}-${intervalo.fin}-${Date.now()}-${Math.random()}`,
                    title: 'Disponible',
                    tipo: 'una vez',
                    start,
                    end,
                    allDay: false,
                    eventDisplay: 'block',
                    className: 'wrap-event-especifico',
                    backgroundColor: '#d33',
                    borderColor: '#d33',
                    textColor: '#ffffff',
                    editable: true,
                });
            });
        });

        setEvents(newEvents);
    }, [cargando]);

    useEffect(() => {
        //Cada vez que cambie events, se actualiza el horario de oficina y el horario especifico
        console.log("Cambiando events:", events);
        setHorarioEspecifico(validateHorario('una vez'));
        setHorarioOficina(validateHorario('fijo'));
    }, [events]);

    useEffect(() => {
        let draggableEl = document.getElementById('external-events');
        if (draggableEl) {
            new Draggable(draggableEl, {
                itemSelector: '.fc-event',
                eventData: function (eventEl) {
                    return {
                        title: eventEl.innerText.trim(),
                        id: Date.now().toString(), // Generar un ID único para cada evento
                        backgroundColor: '#000',
                        borderColor: '#000',
                        textColor: '#ffffff'
                    };
                }
            });
        }
    }, [showExternalEvents]);

    const handleDateClick = (info) => {
        const calendarApi = bigCalendarRef.current.getApi();
        calendarApi.gotoDate(info.date);
    };

    const handleEventClick = (info) => {
        const dia = diasSemana[info.event.start.getDay()];

        Swal.fire({
            title: 'Información Horario',
            html: mensajeHtmlParaAlerta({
                dia: dia,
                tipoHorario: info.event.extendedProps.tipo,
                horaInicio: format(info.event.start, 'HH:mm'),
                horaFin: (info.event.end && format(info.event.end, 'HH:mm') || 'Indefinido')
            }),
            customClass: {
                popup: 'disponibility-swal-custom-popup',
                title: 'disponibility-swal-custom-title',
                htmlContainer: 'disponibility-swal-custom-html',
                confirmButton: 'disponibility-swal-custom-button',
                cancelButton: 'disponibility-swal-cancel-button',
                icon: 'disponibility-swal-custom-icon'
            },
            showCancelButton: true,
            focusCancel: true,
            confirmButtonText: 'Eliminar',
            cancelButtonText: 'Salir'
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire({
                    title: '¿Está seguro?',
                    text: 'No podrá revertir esta acción',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Si, eliminar',
                    cancelButtonText: 'Cancelar',
                    customClass: {
                        popup: 'disponibility-swal-custom-popup',
                        title: 'disponibility-swal-custom-title',
                        confirmButton: 'disponibility-swal-confirm-custom-button',
                        cancelButton: 'disponibility-swal-cancel-button',
                        icon: 'disponibility-swal-custom-icon'
                    }
                }).then((result) => {
                    if (result.isConfirmed) {
                        info.event.remove();
                        setEvents(prevEvents => prevEvents.filter(event => event.id !== info.event.id));
                    }
                })
            }
        });
    };

    // const isCalendarioOficinaOEspecifico = async (info) => {
    //     const dia = diasSemana[info.event.start.getDay()];
    //     const result = await Swal.fire({
    //         title: 'Tipo de horario',
    //         confirmButtonText: `Repetir Dias ${dia}`,
    //         confirmButtonColor: '#3085d6',
    //         showCancelButton: true,
    //         cancelButtonText: 'Evento una vez',
    //         cancelButtonColor: 'rgb(125, 168, 197)',
    //     });
    //     if (result.isConfirmed) {
    //         return 'fijo';
    //     } else {
    //         return 'una vez';
    //     }
    // }

    /* **** INICIO MANEJO DE EVENTOS, (PONER, ARRASTRAR Y REDIMENSIONAR ) ****   */
    //Evento Cuando recien se agrega un horario
    const handleEventReceive = async (info) => {
        console.log("Evento recibido", info.event);
        // const tipo = await isCalendarioOficinaOEspecifico(info);

        info.event.setEnd(new Date(info.event.start.getTime() + 60 * 60000)); // Añade 60 minutos
        const horaInicio = format(info.event.start, 'HH:mm');
        const horaFin = format(info.event.end, 'HH:mm');
        setEvents(prevEvents => [
            ...prevEvents,
            {
                id: info.event.id,
                title: `${horaInicio} - ${horaFin}`,
                start: info.event.start,
                end: info.event.end,
                startTime: horaInicio,
                endTime: horaFin,
                daysOfWeek: [info.event.start.getDay()],
                tipo: 'fijo',
                className: 'wrap-event-oficina',
                backgroundColor: 'rgb(33, 196, 93)',
                borderColor: 'rgb(33, 196, 93)',
                textColor: '#ffffff'
            }
        ]);

    };

    //Evento Cuando se arrastra un evento a otra posición
    const handleEventDrop = (info) => {
        console.log("Evento arrastrado", info.event);
        console.log("Evento arrastrado start y end", info.event.start, info.event.end);

        // Actualizamos el evento con la nueva posición
        setEvents(prevEvents => {
            return prevEvents.map(event => {
                if (event.id === info.event.id && event.tipo === 'fijo') {
                    return {
                        ...event,
                        start: info.event.start,
                        end: info.event.end,
                        startTime: format(info.event.start, 'HH:mm'),
                        endTime: format(info.event.end, 'HH:mm'),
                        daysOfWeek: [info.event.start.getDay()]
                    };
                } else if (event.id === info.event.id && event.tipo === 'una vez') {
                    return {
                        ...event,
                        start: info.event.start,
                        end: info.event.end,
                    };
                }
                return event;
            });
        });
    };


    //Evento Cuando se redimensiona un horario
    const handleEventResize = (info) => {
        console.log("Evento redimensionado", info.event);
        setEvents(prevEvents => {
            return prevEvents.map(event => {
                if (event.id === info.event.id && event.tipo === 'fijo') {
                    return {
                        ...event,
                        start: info.event.start,
                        end: info.event.end,
                        startTime: format(info.event.start, 'HH:mm'),
                        endTime: format(info.event.end, 'HH:mm'),
                        daysOfWeek: [info.event.start.getDay()]
                    };
                } else if (event.id === info.event.id && event.tipo === 'una vez') {
                    return {
                        ...event,
                        start: info.event.start,
                        end: info.event.end,
                    };
                }
                return event;
            });
        });
    };
    /* **** FIN MANEJO DE EVENTOS, (PONER, ARRASTRAR Y REDIMENSIONAR ) **** */

    //Esto es para que se vea el evento arrastrado (estilos)
    const handleEventDragStart = (info) => {
        console.log("Evento arrastrado start", info.event.extendedProps.tipo);
        info.el.classList.add(`fc-event-dragging-${info.event.extendedProps.tipo === 'fijo' ? 'oficina' : 'especifico'}`);
    };

    //Esto es para que se quite el evento arrastrado (estilos)
    const handleEventDragStop = (info) => {
        info.el.classList.remove(`fc-event-dragging-${info.event.extendedProps.tipo === 'fijo' ? 'oficina' : 'especifico'}`);
    };

    //Esto es para que se muestren los eventos en el calendario en la vista de semana y día, no en el del mes
    const handleViewDidMount = (view) => {
        setShowExternalEvents(view.view.type === 'timeGridWeek' || view.view.type === 'timeGridDay' || view.view.type === 'timeGrid3Days');
    };

    const onHandleGuardarCambios = () => {
        alertaCargandoProceso({
            titulo: 'Guardando cambios',
            messageHtml: 'Espere un momento por favor',
            funcionAsync: () => {
                apiClient.post(`/api/doctor/createCalendar/${doctorId}`, { calendarioOficina: horarioOficina, calendarioEspecifico: horarioEspecifico })
                    .then((res) => {
                        console.log(res);
                        alertaGeneral({
                            titulo: 'Calendario guardado',
                            messageHtml: 'Se han guardado los cambios correctamente',
                        });
                        setRefresh(!refresh);
                    }).catch((error) => {
                        alertaGeneral({
                            titulo: 'Error',
                            messageHtml: error.response?.data?.message || 'No se pudieron guardar los cambios',
                            isError: true
                        })
                    });
            },
        })
    }

    const handleAddHorario = () => {
        // Validar que las horas sean válidas
        if (!newHorario.horaInicio || !newHorario.horaFin) {
            alertaGeneral({
                titulo: 'Error',
                messageHtml: 'Por favor ingresa ambas horas (inicio y fin)',
                isError: true
            });
            return;
        }

        // Validar que la hora de fin sea mayor que la de inicio
        const [startH, startM] = newHorario.horaInicio.split(':').map(Number);
        const [endH, endM] = newHorario.horaFin.split(':').map(Number);
        const startMinutes = startH * 60 + startM;
        const endMinutes = endH * 60 + endM;

        if (endMinutes <= startMinutes) {
            alertaGeneral({
                titulo: 'Error',
                messageHtml: 'La hora de fin debe ser mayor que la hora de inicio',
                isError: true
            });
            return;
        }

        const inicioLabel = formatTime12(newHorario.horaInicio);
        const finLabel = formatTime12(newHorario.horaFin);

        let nuevoEvento;
        if (newHorario.soloUnaFecha && newHorario.fechaEspecifica) {
            const start = new Date(newHorario.fechaEspecifica + 'T' + newHorario.horaInicio + ':00');
            const end = new Date(newHorario.fechaEspecifica + 'T' + newHorario.horaFin + ':00');
            nuevoEvento = {
                id: `especifico-new-${Date.now()}`,
                title: 'Disponible',
                tipo: 'una vez',
                start,
                end,
                allDay: false,
                eventDisplay: 'block',
                className: 'wrap-event-especifico',
                backgroundColor: '#d33',
                borderColor: '#d33',
                textColor: '#ffffff',
                editable: true,
            };
        } else {
            const diasSemanaLocal = {
                "lunes": 1,
                "martes": 2,
                "miercoles": 3,
                "jueves": 4,
                "viernes": 5,
                "sabado": 6,
                "domingo": 0
            };
            const diaNumero = diasSemanaLocal[newHorario.dia.toLowerCase()];
            nuevoEvento = {
                id: `fijo-new-${Date.now()}`,
                title: 'Disponible',
                tipo: 'fijo',
                daysOfWeek: [diaNumero],
                startTime: newHorario.horaInicio,
                endTime: newHorario.horaFin,
                allDay: false,
                eventDisplay: 'block',
                className: 'wrap-event-oficina',
                backgroundColor: 'rgb(33, 196, 93)',
                borderColor: 'rgb(33, 196, 93)',
                textColor: '#ffffff',
                editable: true,
            };
        }

        const nextEvents = [...events, nuevoEvento];
        const calendarioOficina = validateHorario('fijo', nextEvents);
        const calendarioEspecifico = validateHorario('una vez', nextEvents);

        alertaCargandoProceso({
            titulo: 'Agregando horario',
            messageHtml: 'Guardando en el calendario…',
            funcionAsync: () => {
                return apiClient.post(`/api/doctor/createCalendar/${doctorId}`, {
                    calendarioOficina,
                    calendarioEspecifico
                })
                    .then((res) => {
                        setEvents(nextEvents);
                        if (newHorario.soloUnaFecha && newHorario.fechaEspecifica) {
                            const fechaLabel = new Date(newHorario.fechaEspecifica + 'T12:00:00').toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' });
                            setHorarioAgregadoMsg({ dia: fechaLabel, inicio: inicioLabel, fin: finLabel, esFechaUnica: true });
                        } else {
                            setHorarioAgregadoMsg({ dia: capitalize(newHorario.dia), inicio: inicioLabel, fin: finLabel, esFechaUnica: false });
                        }
                        setNewHorario({
                            dia: 'lunes',
                            horaInicio: '09:00',
                            horaFin: '17:00',
                            tipo: 'fijo',
                            soloUnaFecha: false,
                            fechaEspecifica: new Date().toISOString().slice(0, 10)
                        });
                        setShowModalAgregarHorario(false);
                        if (timeoutAgregadoRef.current) clearTimeout(timeoutAgregadoRef.current);
                        timeoutAgregadoRef.current = setTimeout(() => setHorarioAgregadoMsg(null), 4500);
                        alertaGeneral({
                            titulo: 'Listo',
                            messageHtml: 'El horario se agregó al calendario correctamente.'
                        });
                    })
                    .catch((error) => {
                        alertaGeneral({
                            titulo: 'Error',
                            messageHtml: error.response?.data?.message || 'No se pudo guardar el horario. Intenta de nuevo.',
                            isError: true
                        });
                    });
            },
        });
    }

    return (
        <>
        <section
            id="seccion-horarios-disponibilidad"
            className={`transition-all duration-1000 rounded-[1.75rem] md:rounded-[2rem] overflow-hidden shadow-[0_8px_30px_-8px_rgba(31,148,194,0.15),0_4px_16px_-6px_rgba(0,0,0,0.08)] border border-[#6e787aff]/30 ${
                highlightSection ? 'ring-4 ring-amber-400 ring-offset-2' : ''
            }`}
        >
            <div className="flex flex-wrap items-center justify-between gap-3 text-white bg-gradient-to-r from-[#1f94c2ff] to-[#205087ff] py-2.5 px-4 md:px-6">
                <h2 className="flex items-center text-sm md:text-base font-medium">
                    <SvgIcon Icon={Calendario} className="w-5 h-5 mr-3 shrink-0" /> Horarios de disponibilidad
                </h2>
                <button
                    type="button"
                    onClick={() => setShowModalAgregarHorario(true)}
                    className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white font-medium text-sm transition-colors border border-white/30"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Agregar Horario
                </button>
            </div>
            {horarioAgregadoMsg && (
                <div
                    role="status"
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-50 border-b border-emerald-100 text-sm text-emerald-800"
                >
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white">
                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                    </span>
                    <span><strong>Horario agregado:</strong> {horarioAgregadoMsg.dia} {horarioAgregadoMsg.inicio} – {horarioAgregadoMsg.fin}. Recuerda guardar los cambios.</span>
                </div>
            )}
            <main className="disponibility flex-grow flex items-center justify-center">
                <div className="w-full bg-white">
                    {cargando ? (

                        <Cargando altura={"20vh"} />

                    ) : (

                        <div className="p-4 md:p-6">
                            <div className="w-full">
                                <FullCalendar
                                    ref={bigCalendarRef}
                                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                                    initialView={calendarView}
                                    locale={esLocale}
                                    dateClick={handleDateClick}
                                    headerToolbar={{
                                        left: 'prev,next today',
                                        center: 'title',
                                        right: 'dayGridMonth,timeGridWeek,timeGridDay'
                                    }}
                                    titleFormat={{ month: 'long', year: 'numeric' }}
                                    allDaySlot={false} // Ocultar la fila de todo el día
                                    expandRows={true}
                                    editable={true}
                                    droppable={true}
                                    events={events}
                                    slotLabelFormat={{
                                        hour: 'numeric',
                                        minute: '2-digit',
                                        meridiem: 'short',
                                        hour12: true
                                    }}
                                    displayEventEnd={true}
                                    eventTimeFormat={{
                                        hour: 'numeric',
                                        minute: '2-digit',
                                        meridiem: 'short',
                                        hour12: true
                                    }}
                                    eventResizableFromStart={true}
                                    eventReceive={handleEventReceive}
                                    eventClick={handleEventClick}
                                    eventDrop={handleEventDrop}
                                    eventResize={handleEventResize}
                                    eventDragStart={handleEventDragStart}
                                    eventDragStop={handleEventDragStop}
                                    eventOverlap={false} // Evitar superposición de eventos
                                    viewDidMount={handleViewDidMount}
                                    slotDuration={'00:20:00'} // Intervalos de 20 minutos
                                    snapDuration={'00:20:00'} // Snap a intervalos de 20 minutos
                                    buttonText={{
                                        timeGrid3Days: '3 Días'
                                    }}
                                    views={{
                                        dayGridMonth: {
                                            eventStartEditable: false,
                                            eventDurationEditable: false,
                                        },
                                        timeGridWeek: {
                                            dayCount: 14,
                                            dayHeaderFormat: { weekday: 'short', day: 'numeric' },
                                        },
                                        timeGrid3Days: {
                                            type: 'timeGrid',
                                            duration: { days: 3 },
                                            dayHeaderFormat: { weekday: 'short', day: 'numeric' },
                                        },
                                        timeGridDay: {
                                            dayHeaderFormat: { weekday: 'short', day: 'numeric' },
                                        },
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </section>
        {createPortal(
            showModalAgregarHorario && (
                <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="modal-agregar-horario-title"
                    onClick={(e) => e.target === e.currentTarget && setShowModalAgregarHorario(false)}
                >
                    <div
                        className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between rounded-t-2xl">
                            <h2 id="modal-agregar-horario-title" className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                                <svg className="w-5 h-5 text-[#1f94c2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                                Agregar Horario
                            </h2>
                            <button
                                type="button"
                                onClick={() => setShowModalAgregarHorario(false)}
                                className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                                aria-label="Cerrar"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="p-5 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    ¿Cuándo aplica este horario?
                                </label>
                                <div className="flex gap-4">
                                    <label className="flex cursor-pointer items-center gap-2">
                                        <input
                                            type="radio"
                                            name="tipoHorarioModal"
                                            checked={!newHorario.soloUnaFecha}
                                            onChange={() => setNewHorario({ ...newHorario, soloUnaFecha: false })}
                                            className="h-4 w-4 border-gray-300 text-[#1f94c2] focus:ring-[#1f94c2]"
                                        />
                                        <span className="text-sm text-gray-700">Cada semana (mismo día)</span>
                                    </label>
                                    <label className="flex cursor-pointer items-center gap-2">
                                        <input
                                            type="radio"
                                            name="tipoHorarioModal"
                                            checked={newHorario.soloUnaFecha}
                                            onChange={() => setNewHorario({ ...newHorario, soloUnaFecha: true })}
                                            className="h-4 w-4 border-gray-300 text-[#1f94c2] focus:ring-[#1f94c2]"
                                        />
                                        <span className="text-sm text-gray-700">Solo una fecha</span>
                                    </label>
                                </div>
                            </div>
                            {!newHorario.soloUnaFecha ? (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Día de la Semana</label>
                                    <select
                                        value={newHorario.dia}
                                        onChange={(e) => setNewHorario({...newHorario, dia: e.target.value})}
                                        className="w-full px-3 py-2 border-2 border-[#1f94c2ff] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1f94c2ff] bg-blue-50 text-sm"
                                    >
                                        <option value="lunes">Lunes</option>
                                        <option value="martes">Martes</option>
                                        <option value="miercoles">Miércoles</option>
                                        <option value="jueves">Jueves</option>
                                        <option value="viernes">Viernes</option>
                                        <option value="sabado">Sábado</option>
                                        <option value="domingo">Domingo</option>
                                    </select>
                                </div>
                            ) : (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Fecha</label>
                                    <input
                                        type="date"
                                        value={newHorario.fechaEspecifica}
                                        onChange={(e) => setNewHorario({ ...newHorario, fechaEspecifica: e.target.value })}
                                        min={new Date().toISOString().slice(0, 10)}
                                        className="w-full px-3 py-2 border-2 border-[#1f94c2ff] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1f94c2ff] bg-blue-50 text-sm"
                                    />
                                    <p className="mt-1 text-xs text-slate-400">Disponibilidad solo para este día</p>
                                </div>
                            )}
                            <div>
                                <label className="block text-sm font-medium text-slate-600 mb-1.5">Hora de Inicio</label>
                                <div className="inline-flex items-center gap-0 rounded-xl border border-slate-200 bg-slate-50/80 p-1 shadow-sm">
                                    <select
                                        value={time24To12(newHorario.horaInicio).hour12}
                                        onChange={(e) => setNewHorario({
                                            ...newHorario,
                                            horaInicio: time12To24(Number(e.target.value), minuteToSlot(time24To12(newHorario.horaInicio).minute), time24To12(newHorario.horaInicio).ampm)
                                        })}
                                        className="rounded-lg border-0 bg-transparent px-3 py-2 text-sm font-medium text-slate-800 focus:ring-2 focus:ring-[#1f94c2]/40"
                                    >
                                        {HOURS_12.map(h => <option key={h} value={h}>{h}</option>)}
                                    </select>
                                    <span className="text-slate-400 font-medium">:</span>
                                    <select
                                        value={minuteToSlot(time24To12(newHorario.horaInicio).minute)}
                                        onChange={(e) => setNewHorario({
                                            ...newHorario,
                                            horaInicio: time12To24(time24To12(newHorario.horaInicio).hour12, Number(e.target.value), time24To12(newHorario.horaInicio).ampm)
                                        })}
                                        className="rounded-lg border-0 bg-transparent px-2 py-2 text-sm font-medium text-slate-800 focus:ring-2 focus:ring-[#1f94c2]/40 w-14"
                                    >
                                        {MINUTES_OPTIONS.map(m => <option key={m} value={m}>{String(m).padStart(2, '0')}</option>)}
                                    </select>
                                    <select
                                        value={time24To12(newHorario.horaInicio).ampm}
                                        onChange={(e) => setNewHorario({
                                            ...newHorario,
                                            horaInicio: time12To24(time24To12(newHorario.horaInicio).hour12, minuteToSlot(time24To12(newHorario.horaInicio).minute), e.target.value)
                                        })}
                                        className="rounded-lg border-0 bg-transparent pl-2 pr-3 py-2 text-sm font-semibold text-slate-800 focus:ring-2 focus:ring-[#1f94c2]/40"
                                    >
                                        <option value="AM">AM</option>
                                        <option value="PM">PM</option>
                                    </select>
                                </div>
                                <p className="mt-1 text-xs text-slate-400">Bloques de 20 min</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-600 mb-1.5">Hora de Fin</label>
                                <div className="inline-flex items-center gap-0 rounded-xl border border-slate-200 bg-slate-50/80 p-1 shadow-sm">
                                    <select
                                        value={time24To12(newHorario.horaFin).hour12}
                                        onChange={(e) => setNewHorario({
                                            ...newHorario,
                                            horaFin: time12To24(Number(e.target.value), minuteToSlot(time24To12(newHorario.horaFin).minute), time24To12(newHorario.horaFin).ampm)
                                        })}
                                        className="rounded-lg border-0 bg-transparent px-3 py-2 text-sm font-medium text-slate-800 focus:ring-2 focus:ring-[#1f94c2]/40"
                                    >
                                        {HOURS_12.map(h => <option key={h} value={h}>{h}</option>)}
                                    </select>
                                    <span className="text-slate-400 font-medium">:</span>
                                    <select
                                        value={minuteToSlot(time24To12(newHorario.horaFin).minute)}
                                        onChange={(e) => setNewHorario({
                                            ...newHorario,
                                            horaFin: time12To24(time24To12(newHorario.horaFin).hour12, Number(e.target.value), time24To12(newHorario.horaFin).ampm)
                                        })}
                                        className="rounded-lg border-0 bg-transparent px-2 py-2 text-sm font-medium text-slate-800 focus:ring-2 focus:ring-[#1f94c2]/40 w-14"
                                    >
                                        {MINUTES_OPTIONS.map(m => <option key={m} value={m}>{String(m).padStart(2, '0')}</option>)}
                                    </select>
                                    <select
                                        value={time24To12(newHorario.horaFin).ampm}
                                        onChange={(e) => setNewHorario({
                                            ...newHorario,
                                            horaFin: time12To24(time24To12(newHorario.horaFin).hour12, minuteToSlot(time24To12(newHorario.horaFin).minute), e.target.value)
                                        })}
                                        className="rounded-lg border-0 bg-transparent pl-2 pr-3 py-2 text-sm font-semibold text-slate-800 focus:ring-2 focus:ring-[#1f94c2]/40"
                                    >
                                        <option value="AM">AM</option>
                                        <option value="PM">PM</option>
                                    </select>
                                </div>
                                <p className="mt-1 text-xs text-slate-400">Bloques de 20 min</p>
                            </div>
                        </div>
                        <div className="sticky bottom-0 bg-slate-50 px-5 py-4 flex gap-3 rounded-b-2xl border-t border-slate-200">
                            <button
                                type="button"
                                onClick={() => setShowModalAgregarHorario(false)}
                                className="flex-1 px-4 py-2.5 rounded-xl border border-slate-300 text-slate-700 font-medium text-sm hover:bg-slate-100 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={handleAddHorario}
                                className="flex-1 px-4 py-2.5 rounded-xl bg-[#1f94c2] hover:bg-[#205087] text-white font-semibold text-sm shadow-md hover:shadow-lg transition-all"
                            >
                                + Agregar Horario
                            </button>
                        </div>
                    </div>
                </div>
            ),
            document.body
        )}
        </>
    );

    function mensajeHtmlParaAlerta({ dia, tipoHorario, horaInicio, horaFin }) {
        return `Dia: ${dia} <br>
            Tipo Horario: ${tipoHorario} <br>
            Hora Inicio: ${horaInicio} <br>
            Hora Fin: ${horaFin}`;
    }

    function validateHorario(tipo, eventsList = null) {
        const list = eventsList != null ? eventsList : events;
        let horarios = {};
        list.filter(event => event.tipo === tipo).forEach(horario => {
            if (tipo === 'fijo') {
                const diaSemana = diasSemana[horario.daysOfWeek ? horario.daysOfWeek[0] : new Date(horario.start).getDay()];
                const intervalo = {
                    inicio: horario.startTime,
                    fin: horario.endTime
                };

                if (horarios[diaSemana]) {
                    const yaExiste = horarios[diaSemana].intervalos.some(
                        i => i.inicio === intervalo.inicio && i.fin === intervalo.fin
                    );
                    if (!yaExiste) horarios[diaSemana].intervalos.push(intervalo);
                } else {
                    horarios[diaSemana] = {
                        dia: diaSemana,
                        intervalos: [intervalo]
                    };
                }
            } else if (tipo === 'una vez') {
                const dia = new Date(horario.start).toISOString().split('T')[0]; // Obtén la fecha en formato "YYYY-MM-DD"
                const intervalo = { inicio: format(new Date(horario.start), 'HH:mm'), fin: format(new Date(horario.end), 'HH:mm') };

                if (horarios[dia]) {
                    horarios[dia].intervalos.push(intervalo);
                } else {
                    horarios[dia] = {
                        dia: dia,
                        intervalos: [intervalo]
                    };
                }
            }
        });
        return Object.values(horarios);
    }

};
export default CalendarDisponibility;